import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * Team → Reps (BACKLOG C-14 phase 2, W3, GLE-8394): a workspace's callers for
 * its Admins and LGA. Behind the backend switch `user_roles_reps_enabled`:
 * while it is off every route answers 404 with error_code 1035, which the
 * page reads as "not switched on". The server checks every request.
 */

export interface ManageRepCampaignI {
  id: string;
  name: string;
}

export interface ManageRepI {
  user_id: string;
  name: string;
  email: string;
  /** False while the caller is deactivated on the workspace. */
  active: boolean;
  deactivated_at: string | null;
  deactivated_by_name: string | null;
  deactivation_note: string | null;
  /** Live strikes on the workspace's campaigns; absent while strikes are off. */
  live_strikes?: number;
  blocks_per_week: number;
  /** Workspace campaigns the caller may work. */
  approved_campaign_ids: Array<string>;
  /** Workspace campaigns the caller was blocked from. */
  blocked_campaign_ids: Array<string>;
  pod: { id: string; name: string } | null;
}

export interface ManageRepsI {
  campaigns: Array<ManageRepCampaignI>;
  reps: Array<ManageRepI>;
}

export interface ManageRepWriteI {
  rep: ManageRepI;
  /** The caller's future blocks the write cancelled. */
  cancelled_blocks: number;
}

export interface ManageRepMoveI extends ManageRepWriteI {
  from_workspace_id: string | null;
}

export interface MoveRepRequestI {
  workspace_id: string;
  /** Default true: also leave the old workspace's campaigns and blocks. */
  release_old_campaigns?: boolean;
  note?: string;
}

/**
 * Admin → Rep workspaces (C-16 S5, GLE-8416): every rep across workspaces,
 * the ones with no workspace first. Admins only; behind the same switch.
 */
export interface ManageAdminRepI {
  user_id: string;
  name: string;
  email: string;
  /** Null while the rep has no workspace. */
  workspace_id: string | null;
  workspace_name: string | null;
  /** False while the rep is deactivated on their workspace. */
  active: boolean;
  application_status: string | null;
  /** When their user account was created (no acceptance time is recorded). */
  joined_at: string;
}

export const MANAGE_ADMIN_REP_VIEWS = {
  ALL: "all",
  UNASSIGNED: "unassigned",
} as const;

export type ManageAdminRepViewI =
  (typeof MANAGE_ADMIN_REP_VIEWS)[keyof typeof MANAGE_ADMIN_REP_VIEWS];

export interface ManageAdminRepsParamsI {
  view?: ManageAdminRepViewI;
  workspace_id?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface ManageAdminRepsI {
  reps: Array<ManageAdminRepI>;
  /** How many reps match the filter. */
  total: number;
  /** Every rep with no workspace, whatever the filter. */
  unassigned_total: number;
  limit: number;
  offset: number;
}

export interface ManageRepWorkspaceI {
  id: string;
  name: string;
  rep_count: number;
}

// A 404 is the switch being off: an answer the page and the nav probe act on,
// not an error to log on every load. Built per call: the request interceptor
// writes into `meta`.
const switchProbeConfig = () =>
  ({
    meta: { expectedStatuses: [404] },
  }) as AxiosRequestConfig & AxiosInstanceInterceptorRequestExtensionI;

const repsPath = (workspaceId: string) =>
  `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/reps`;

export const ManageRepsAPIEndpoints = (api: AxiosInstance) => ({
  listReps: (workspaceId: string) =>
    api.get<ManageRepsI>(repsPath(workspaceId), switchProbeConfig()),

  /** Approves the caller on the campaign, or takes the approval away. */
  setRepCampaign: (
    workspaceId: string,
    userId: string,
    campaignId: string,
    approved: boolean
  ) =>
    api.put<ManageRepWriteI>(
      `${repsPath(workspaceId)}/${encodeURIComponent(
        userId
      )}/campaigns/${encodeURIComponent(campaignId)}`,
      { approved }
    ),

  /** Deactivates (false) or reactivates (true) the caller on the workspace. */
  setRepActive: (
    workspaceId: string,
    userId: string,
    active: boolean,
    note?: string
  ) =>
    api.put<ManageRepWriteI>(
      `${repsPath(workspaceId)}/${encodeURIComponent(userId)}/active`,
      note ? { active, note } : { active }
    ),

  /** Admins: every rep, unassigned first (C-16 S5). */
  listAdminReps: (params: ManageAdminRepsParamsI) =>
    api.get<ManageAdminRepsI>("/v1/manage/reps", {
      ...switchProbeConfig(),
      params,
    }),

  /** Admins: every workspace a rep can go to, most reps first (C-16 S5). */
  listRepWorkspaces: () =>
    api.get<{ workspaces: Array<ManageRepWorkspaceI> }>(
      "/v1/manage/reps/workspaces",
      switchProbeConfig()
    ),

  /** Admins: moves the caller to another workspace, or assigns one. */
  moveRep: (userId: string, body: MoveRepRequestI) =>
    api.put<ManageRepMoveI>(
      `/v1/manage/reps/${encodeURIComponent(userId)}/workspace`,
      body
    ),
});
